import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing your invite...');

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');

      if (accessToken && refreshToken) {
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError) {
          console.error('Session error:', sessionError);
          setStatus('error');
          setMessage('Failed to verify your authentication. Please try again or contact support.');
          return;
        }
      }

      const { data: { session }, error: getSessionError } = await supabase.auth.getSession();

      if (getSessionError) {
        console.error('Get session error:', getSessionError);
        setStatus('error');
        setMessage('Failed to verify your session. Please try again or contact support.');
        return;
      }

      if (!session) {
        setStatus('error');
        setMessage('No active session found. Please check your link.');
        return;
      }

      if (type === 'recovery') {
        setStatus('success');
        setMessage('Redirecting to password reset...');
        setTimeout(() => {
          navigate('/auth/set-password?mode=recovery');
        }, 1000);
        return;
      }

      const user = session.user;
      const tenantId = user.user_metadata?.tenant_id;
      const tenantSlug = user.user_metadata?.tenant_slug;
      const passwordSet = user.user_metadata?.password_set;

      if (!tenantId || !tenantSlug) {
        setStatus('error');
        setMessage('Invalid invite data. Please contact support.');
        return;
      }

      if (!passwordSet) {
        setStatus('success');
        setMessage('Redirecting to password setup...');
        setTimeout(() => {
          navigate('/auth/set-password');
        }, 1000);
        return;
      }

      const adminApiUrl = import.meta.env.VITE_ADMIN_API_URL || 'https://api.medsystem.app';
      
      try {
        const response = await fetch(`${adminApiUrl}/api/tenants/${tenantId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          console.error('Failed to fetch tenant:', response.status);
        }
      } catch (err) {
        console.error('Error fetching tenant:', err);
      }

      setStatus('success');
      setMessage('Welcome! Redirecting to your dashboard...');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error) {
      console.error('Auth callback error:', error);
      setStatus('error');
      setMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-teal-600 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Invite</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome!</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
